// The backend's storage proxy — pictures served from Trusstor's storage
// gateway. Web and mobile bind this to their own transport.

import { parseStandardErrors } from './monitor.js';
import type { AuthedFetch } from './transport.js';

export function createStorageApi(authedFetch: AuthedFetch) {
  /** SVG text, or null when storage has none (404) — the caller then shows
   *  nothing (or a "none" state) rather than failing. */
  async function svgOrNull(path: string, signal?: AbortSignal): Promise<string | null> {
    const res = await authedFetch(path, { signal });
    if (res.status === 404) return null;
    if (!res.ok) return parseStandardErrors(res);
    return res.text();
  }

  /**
   * The per-trade pictogram (the icon Trusstor stamps inside the coloured
   * profession chip), or null when the trade has none — the chip then renders
   * without an icon.
   */
  function fetchTradeIconSvg(siteId: string, tradeId: string, signal?: AbortSignal): Promise<string | null> {
    return svgOrNull(
      `/api/sites/${encodeURIComponent(siteId)}/storage/trade-icon/${encodeURIComponent(tradeId)}`,
      signal,
    );
  }

  /** A floor's plan, or null when the project has none — the issue form's
   *  location picker shows a "no floor plan" state. */
  function fetchFloorViewSvg(siteId: string, floorId: string, signal?: AbortSignal): Promise<string | null> {
    return svgOrNull(
      `/api/sites/${encodeURIComponent(siteId)}/storage/floors/${encodeURIComponent(floorId)}/view`,
      signal,
    );
  }

  return { fetchTradeIconSvg, fetchFloorViewSvg };
}
